
import Vue from 'vue'
import { Component, Prop } from 'vue-property-decorator'
import { Cargo } from '@/models/Cargo'
import { Transport } from '@/models/Transport'
import { CargoType } from '@/models/CargoType'
import { Model } from '@/models/Model'
import { LCIReport } from '@/models/LCIReport'
import LocationSelector from '@/components/LocationSelector.vue'
import GeoDirection from '@/directions/types/GeoDirection'
import FacilityDirection from '@/directions/types/FacilityDirection'
import { CustomLocation } from '@/models/CustomLocation'
import { TerminalSrsLocation } from '@/models/TerminalSrsLocation'
import { Terminal } from '@/models/Terminal'
import { GeoJSON } from 'geojson'

@Component({
	components: {
		LocationSelector
	}
})
export default class LCIReportGenerator extends Vue {
	@Prop({ required: true })
	transport!: Transport

	cargo: Cargo | null = null
	cargoTypes: Array<CargoType> = []
	skipperName: string | null = null
	client: string | null = null
	vesselName: string | null = null

	customStartLocationPlace: string | null = null
	customDestinationLocationPlace: string | null = null

	loadingPlace: GeoDirection | FacilityDirection | null = null
	unloadingPlace: GeoDirection | FacilityDirection | null = null

	isDocumentGenerating: boolean = false

	cargoTypeLastTransported: { value; text } | string = ''
	cargoTypeSecondLastTransported: { value; text } | string = ''
	cargoTypeThirdLastTransported: { value; text } | string = ''

	async created() {
		this.cargo = await this.transport.fetchCargoLazy()

		this.setLastTransportedCargos()
		await this.setLocations(this.cargo!)
		this.skipperName = this.transport.getActiveVessel()?.skipperName ?? null
		this.vesselName = this.transport.getActiveVessel()?.name ?? null
		this.client = this.cargo?.getClient()?.name ?? null

		this.cargoTypes = await CargoType.fetchAllLazy()
	}

	private mapItemsForCombobox(
		items: Array<Model>,
		attribute: string | any = 'title'
	): Array<{ value: string; text: string }> {
		return items.map((item) => {
			let text = ''
			if (typeof attribute === 'string') {
				text = item[attribute]
			} else {
				text = attribute(item)
			}

			return {
				value: item.getApiId() as string,
				text
			}
		})
	}

	setLastTransportedCargos() {
		const cargoTypeLastTransported = (
			this.cargo!.getCharterTerms() ?? this.cargo!.getOrderTerms()
		).getCargoTypeLastTransported()
		if (cargoTypeLastTransported) {
			this.cargoTypeLastTransported = {
				value: cargoTypeLastTransported.getApiId(),
				text: cargoTypeLastTransported.title
			}
		}

		const cargoTypeSecondLastTransported = (
			this.cargo!.getCharterTerms() ?? this.cargo!.getOrderTerms()
		).getCargoTypeSecondLastTransported()
		if (cargoTypeSecondLastTransported) {
			this.cargoTypeSecondLastTransported = {
				text: cargoTypeSecondLastTransported.title,
				value: cargoTypeSecondLastTransported.getApiId()
			}
		}

		const cargoTypeThirdLastTransported = (
			this.cargo!.getCharterTerms() ?? this.cargo!.getOrderTerms()
		).getCargoTypeThirdLastTransported()
		if (cargoTypeThirdLastTransported) {
			this.cargoTypeThirdLastTransported = {
				text: cargoTypeThirdLastTransported.title,
				value: cargoTypeThirdLastTransported.getApiId()
			}
		}
	}

	async setLocations(cargo: Cargo) {
		if (this.cargo!.getStartTerminal()) {
			const terminal = this.cargo!.getStartTerminal()
			this.loadingPlace = FacilityDirection.fromTerminal(terminal)
		} else if (this.cargo?.getCustomStartLocation()) {
			this.customStartLocationPlace = this.cargo.getCustomStartLocation().getName()
			const customStartLocation = (
				await CustomLocation.find(this.cargo.getCustomStartLocation().getApiId() as string)
			).getData() as CustomLocation

			this.loadingPlace = GeoDirection.fromCustomLocation(customStartLocation)
		} else if (this.cargo?.getStartTerminalSrsLocation) {
			const terminalSrsLocation = (
				await TerminalSrsLocation.find(this.cargo.getStartTerminalSrsLocation().getApiId() as string)
			).getData() as TerminalSrsLocation

			this.loadingPlace = FacilityDirection.fromTerminalSrsLocation(terminalSrsLocation)
		} else {
			const direction = new GeoDirection()
			direction.geo = cargo.customStartLocationGeo as GeoJSON.Point
			direction.country = cargo.customStartLocationCountryCode ?? ''
			direction.text = cargo.customStartLocationPlace ?? ''

			this.loadingPlace = direction
		}

		if (this.cargo!.getDestinationTerminal()) {
			const terminal = this.cargo!.getDestinationTerminal()
			this.unloadingPlace = FacilityDirection.fromTerminal(terminal)
		} else if (this.cargo?.getCustomDestinationLocation()) {
			this.customDestinationLocationPlace = this.cargo.getCustomDestinationLocation().getName()
			const customDestinationLocation = (
				await CustomLocation.find(this.cargo.getCustomDestinationLocation().getApiId() as string)
			).getData() as CustomLocation

			this.unloadingPlace = GeoDirection.fromCustomLocation(customDestinationLocation)
		} else if (this.cargo?.getDestinationTerminalSrsLocation) {
			const terminalSrsLocation = (
				await TerminalSrsLocation.find(this.cargo.getDestinationTerminalSrsLocation().getApiId() as string)
			).getData() as TerminalSrsLocation

			this.unloadingPlace = FacilityDirection.fromTerminalSrsLocation(terminalSrsLocation)
		} else {
			const direction = new GeoDirection()
			direction.geo = cargo.customDestinationGeo as GeoJSON.Point
			direction.country = cargo.customDestinationCountryCode ?? ''
			direction.text = cargo.customDestinationPlace ?? ''

			this.unloadingPlace = direction
		}
	}

	async generateReport() {
		const valid = await this.$validator.validateAll()
		if (!valid) return

		this.isDocumentGenerating = true

		try {
			const lci = await this.prepareLCIReport()
			await lci.save()

			this.closeDocumentCreator()
		} finally {
			this.isDocumentGenerating = false
		}
	}

	async prepareLCIReport(): Promise<LCIReport> {
		const lci = new LCIReport()
		lci.skipperName = this.skipperName!
		lci.client = this.client!
		lci.setTransport(this.transport)

		await this.prepareLocationsForLCI(lci)

		if (typeof this.cargoTypeLastTransported === 'object') {
			lci.setCargoTypeLastTransported(await CargoType.fetchLazy(this.cargoTypeLastTransported.value))
		}
		if (typeof this.cargoTypeSecondLastTransported === 'object') {
			lci.setCargoTypeSecondLastTransported(await CargoType.fetchLazy(this.cargoTypeSecondLastTransported.value))
		}
		if (typeof this.cargoTypeThirdLastTransported === 'object') {
			lci.setCargoTypeThirdLastTransported(await CargoType.fetchLazy(this.cargoTypeThirdLastTransported.value))
		}

		return lci
	}

	async prepareLocationsForLCI(lci) {
		if (this.loadingPlace instanceof FacilityDirection) {
			if (this.loadingPlace.facilityType === 'terminals') {
				const startTerminal = await Terminal.fetchLazy(this.loadingPlace.facilityId)
				lci.setStartTerminal(startTerminal)
				lci.customStartLocationPlace = null
			} else if (this.loadingPlace.facilityType === 'terminalSrsLocations') {
				const startTerminalSrsLocation = (
					await TerminalSrsLocation.find(this.loadingPlace.facilityId)
				).getData() as TerminalSrsLocation
				lci.setStartTerminalSrsLocation(startTerminalSrsLocation)
				lci.customStartLocationPlace = null
			}
		} else if (this.loadingPlace instanceof GeoDirection && this.customStartLocationPlace) {
			const customStartLocation = (
				await CustomLocation.find(this.cargo!.getCustomStartLocation().getApiId() as string)
			).getData() as CustomLocation
			lci.setCustomStartLocation(customStartLocation)
		} else if (this.loadingPlace instanceof GeoDirection) {
			lci.customStartLocationPlace = this.loadingPlace.text
		}

		if (this.unloadingPlace instanceof FacilityDirection) {
			if (this.unloadingPlace.facilityType === 'terminals') {
				const destinationTerminal = await Terminal.fetchLazy(this.unloadingPlace.facilityId)
				lci.setDestinationTerminal(destinationTerminal)
				lci.customDestinationLocationPlace = null
			} else if (this.unloadingPlace.facilityType === 'terminalSrsLocations') {
				const destinationTerminalSrsLocation = (
					await TerminalSrsLocation.find(this.unloadingPlace.facilityId)
				).getData() as TerminalSrsLocation
				lci.setDestinationTerminalSrsLocation(destinationTerminalSrsLocation)
				lci.customDestinationLocationPlace = null
			}
		} else if (this.unloadingPlace instanceof GeoDirection && this.customDestinationLocationPlace) {
			const customDestinationLocation = (
				await CustomLocation.find(this.cargo!.getCustomDestinationLocation().getApiId() as string)
			).getData() as CustomLocation
			lci.setCustomStartLocation(customDestinationLocation)
		} else if (this.unloadingPlace instanceof GeoDirection) {
			lci.customDestinationLocationPlace = this.unloadingPlace.text
		}
	}

	closeDocumentCreator() {
		this.$emit('creator-closed')
	}
}
